import React from 'react';
import { Link, useMatch, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import supportService from '@/services/supportService';
import { TicketStatus } from '@/types';
import TicketDetailContent from './TicketDetailContent';

const getStatusColor = (status: TicketStatus) => {
  switch (status) {
    case TicketStatus.OPEN: return 'bg-blue-100 text-blue-700';
    case TicketStatus.IN_PROGRESS: return 'bg-orange-100 text-orange-700';
    case TicketStatus.RESOLVED: return 'bg-green-100 text-green-700';
    case TicketStatus.CLOSED: return 'bg-gray-100 text-gray-700';
    default: return 'bg-gray-100 text-gray-700';
  }
};

const getStatusLabel = (status: TicketStatus) => {
  switch (status) {
    case TicketStatus.OPEN: return 'Ouvert';
    case TicketStatus.IN_PROGRESS: return 'En cours';
    case TicketStatus.RESOLVED: return 'Résolu';
    case TicketStatus.CLOSED: return 'Fermé';
    default: return status;
  }
};

const DetailPlaceholder: React.FC = () => (
  <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-3 text-center px-6">
    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
      <MaterialIcon icon="forum" style={{ fontSize: 32 }} />
    </div>
    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Sélectionnez un ticket</h2>
    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
      Cliquez sur un ticket pour voir l'historique des échanges avec le support.
    </p>
  </div>
);

const SupportListPage: React.FC = () => {
  const navigate = useNavigate();
  const detailMatch = useMatch('/support/tickets/:id');
  const selectedId = detailMatch?.params.id;
  const isDetailView = !!selectedId;

  const { data, isLoading } = useQuery({
    queryKey: ['tickets'],
    queryFn: () => supportService.getTickets(),
  });

  const tickets = data?.tickets || [];

  const handleBack = () => {
    if (isDetailView) navigate('/support');
    else navigate('/dashboard');
  };

  return (
    <AppShell
      title={isDetailView ? 'Détail ticket' : 'Support Client'}
      showBack
      backLabel={isDetailView ? 'Support' : 'Accueil'}
      onBack={handleBack}
      wide
    >
      <div className="flex flex-col md:flex-row md:gap-6 md:pt-4">
        {/* List column */}
        <aside
          className={`flex flex-col gap-6 p-4 md:p-0 md:w-[400px] md:shrink-0 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto md:pr-2 ${
            isDetailView ? 'hidden md:flex' : 'flex'
          }`}
        >
          {/* CTA card */}
          <section className="bg-primary/10 rounded-2xl p-6 flex flex-col items-center text-center gap-4">
            <h2 className="font-bold text-lg text-gray-900 dark:text-white">Besoin d'aide ?</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Notre équipe est là pour vous accompagner du Lundi au Vendredi de 8h à 18h.
            </p>
            <div className="flex flex-col gap-2 w-full">
              <a
                href="tel:+221338000000"
                className="flex items-center justify-center gap-3 bg-white dark:bg-surface-dark py-3 px-4 rounded-xl border border-primary/20 shadow-sm text-primary font-bold"
              >
                <MaterialIcon icon="phone" />
                +221 33 800 00 00
              </a>
              <a
                href="mailto:contact@leeris.sn"
                className="flex items-center justify-center gap-3 bg-white dark:bg-surface-dark py-3 px-4 rounded-xl border border-primary/20 shadow-sm text-primary font-bold"
              >
                <MaterialIcon icon="mail" />
                contact@leeris.sn
              </a>
            </div>
            <div className="flex items-center gap-4 w-full py-2">
              <div className="h-px flex-1 bg-primary/10" />
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">OU</span>
              <div className="h-px flex-1 bg-primary/10" />
            </div>
            <button
              type="button"
              onClick={() => navigate('/support/new')}
              className="w-full bg-primary text-[#102216] font-bold py-3.5 rounded-xl hover:bg-[#25d360] transition-colors shadow-md"
            >
              Contacter le support
            </button>
          </section>

          {/* Tickets list */}
          <section>
            <h3 className="font-bold text-gray-900 dark:text-white mb-4 uppercase text-xs tracking-wider opacity-60">
              Mes demandes en cours
            </h3>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div key={i} className="h-24 w-full bg-gray-100 dark:bg-white/5 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : tickets.length === 0 ? (
              <p className="text-center text-gray-400 py-8 italic">Aucun ticket pour le moment</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {tickets.map((ticket) => {
                  const isSelected = ticket.id === selectedId;
                  return (
                    <li key={ticket.id}>
                      <Link
                        to={`/support/tickets/${ticket.id}`}
                        aria-current={isSelected ? 'page' : undefined}
                        className={`block bg-white dark:bg-surface-dark p-4 rounded-xl border transition-all active:scale-[0.99] ${
                          isSelected
                            ? 'border-primary ring-2 ring-primary/30'
                            : 'border-gray-100 dark:border-white/5 hover:border-primary/30 hover:shadow-md'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-bold text-gray-400">#{ticket.ticketNumber || 'SUP-000'}</span>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(ticket.status)}`}>
                            {getStatusLabel(ticket.status)}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-900 dark:text-white line-clamp-1">{ticket.subject}</h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-1">{ticket.description}</p>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </aside>

        {/* Detail column */}
        <section
          className={`flex-1 p-4 md:p-0 md:max-h-[calc(100vh-6rem)] md:overflow-y-auto ${
            isDetailView ? 'flex flex-col' : 'hidden md:flex md:flex-col'
          }`}
        >
          {selectedId ? <TicketDetailContent id={selectedId} /> : <DetailPlaceholder />}
        </section>
      </div>
    </AppShell>
  );
};

export default SupportListPage;
